// "Hay una versión nueva" for a tab left open (docs/PARITY.md P004) — the natives' update banner
// (Mac `MacUpdater` banner, Windows `UpdateBanner`, Android's strip) with the web's install step: a
// reload. Polls the deploy's `version.txt` (core/appUpdateCheck.ts): on coming back to the tab past
// the 10-min floor, and every 15 min while the tab is visible. «Qué hay de nuevo» shows the notes
// newer than the running build when Core has any (§11.3); «Ahora no» hides THAT version only.
import { useCallback, useEffect, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { UpdateBannerDecision, UpdateCheckThrottle } from '../../../core/appUpdateCheck';
import { ReleaseNotesSchedule, type AppReleaseNotes } from '../../../core/releaseNotes';
import { t, tf } from '../../../i18n/i18n';
import { ReleaseNotesDialog, RUNNING_BUILD } from './releaseNotes';
import { PwaIcon } from './pwaIcons';
import './pwa.css';

const DISMISSED_KEY = 'satori.update.dismissed';
export const RUNNING_COMMIT: string = typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : '';
/** Time for the composer's 400 ms draft debounce to land in the store before the page goes away. */
const FLUSH_MS = 650;

function dismissed(): string | null {
  try {
    return localStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
}

async function servedVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}version.txt?t=${Date.now()}`, { cache: 'no-store', credentials: 'omit' });
    return res.ok ? await res.text() : null;
  } catch {
    return null;
  }
}

export function UpdateBanner() {
  const [offered, setOffered] = useState<string | null>(null);
  const [notes, setNotes] = useState<AppReleaseNotes[]>([]);
  const [showNotes, setShowNotes] = useState(false);
  const [reloading, setReloading] = useState(false);
  const lastCheck = useRef<number | null>(null);

  const check = useCallback(async (force = false) => {
    if (!RUNNING_COMMIT || (!import.meta.env.PROD && !force)) return;
    const now = Date.now();
    if (!force && !UpdateCheckThrottle.shouldCheck(lastCheck.current, now)) return;
    lastCheck.current = now;
    const next = UpdateBannerDecision.offered(RUNNING_COMMIT, await servedVersion(), dismissed());
    setOffered(next);
    if (next && RUNNING_BUILD > 0) {
      // The offer's «Qué hay de nuevo»: what Core published after the running build (newest first).
      const r = await data.call('releaseNotes', RUNNING_BUILD, null, ReleaseNotesSchedule.historyLimit).catch(() => null);
      setNotes(r && r.status === 'ok' ? r.releases.filter((x) => x.versionCode > RUNNING_BUILD) : []);
    }
  }, []);

  useEffect(() => {
    // First look a little after start (never on the first frame), then the natives' cadence.
    const first = setTimeout(() => void check(), 20_000);
    const loop = setInterval(() => document.visibilityState === 'visible' && void check(), UpdateCheckThrottle.recheckIntervalMs);
    const onVisible = () => document.visibilityState === 'visible' && void check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    // Test/diagnostic hook: run a check now (bypasses the 10-min floor; scripts/pwa-e2e.mjs).
    (window as unknown as { __satoriCheckUpdate?: () => Promise<void> }).__satoriCheckUpdate = () => check(true);
    return () => {
      clearTimeout(first);
      clearInterval(loop);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [check]);

  if (!offered) return null;
  const version = notes[0]?.versionName ?? null;
  return (
    <>
      <div className="update-banner" role="status" data-testid="update-banner">
        <span className="update-banner-icon">
          <PwaIcon name="arrowUpCircle" size={18} strokeWidth={1.9} />
        </span>
        <div className="update-banner-text">
          <strong>{version ? tf('web.update.availableVersion', version) : t('web.update.available')}</strong>
          <span>{t('web.update.hint')}</span>
        </div>
        <div className="update-banner-actions">
          {notes.length ? (
            <button type="button" className="update-banner-link" onClick={() => setShowNotes(true)}>
              {t('releaseNotes.offerTitle')}
            </button>
          ) : null}
          <button
            type="button"
            className="update-banner-later"
            onClick={() => {
              try {
                localStorage.setItem(DISMISSED_KEY, offered);
              } catch {
                /* a per-viewer convenience */
              }
              setOffered(null);
            }}
          >
            {t('mac.updateNotNow')}
          </button>
          <button
            type="button"
            className="update-banner-reload"
            disabled={reloading}
            onClick={() => {
              setReloading(true);
              // Blur first so a focused composer's pending draft write goes out, then reload.
              (document.activeElement as HTMLElement | null)?.blur?.();
              setTimeout(() => window.location.reload(), FLUSH_MS);
            }}
          >
            {t('web.update.reload')}
          </button>
        </div>
      </div>
      {showNotes ? <ReleaseNotesDialog title={t('releaseNotes.offerTitle')} releases={notes} onClose={() => setShowNotes(false)} /> : null}
    </>
  );
}
